import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { GitMerge, Loader2, MoreHorizontal } from "lucide-react";
import {
  AllCommunityModule,
  ModuleRegistry,
  type ColDef,
  type GetRowIdParams,
  type GridApi,
  type GridReadyEvent,
  type ICellRendererParams,
  type IRowNode,
  type RowSelectionChangedEvent,
} from "ag-grid-community";
import { RowGroupingModule } from "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useBreadcrumb } from "@/contexts/BreadcrumbContext";
import { instagramProfileUrlFromHandle } from "@/lib/instagramProfileUrl";

ModuleRegistry.registerModules([AllCommunityModule, RowGroupingModule]);

interface DuplicateLead {
  _id: string;
  firstName?: string;
  lastName?: string;
  name?: string;
  email?: string;
  phoneNumber?: string;
  instagram?: {
    igUserId?: string;
    igUsername?: string;
  };
  manychat?: {
    id?: string;
  };
  joinedAt?: string;
}

interface DuplicateGroup {
  phoneNumber: string;
  leads: DuplicateLead[];
  mergedLead: DuplicateLead | null;
}

interface PhoneNumberMergeRow extends DuplicateLead {
  groupPhoneNumber: string;
}

interface PhoneNumberMergeGridContext {
  isMerging: boolean;
  mergedLeadsByPhoneNumber: Map<string, DuplicateLead>;
  onMergeSingle: (phoneNumber: string) => void;
}

function getLeadName(lead: DuplicateLead): string {
  const parts = [lead.firstName, lead.lastName].filter(Boolean);
  if (parts.length > 0) return parts.join(" ");
  if (lead.name) return lead.name;
  return "-";
}

function getRowLead({
  data,
  node,
  context,
}: {
  data?: PhoneNumberMergeRow;
  node?: IRowNode<PhoneNumberMergeRow> | null;
  context?: PhoneNumberMergeGridContext;
}): DuplicateLead | undefined {
  if (data) return data;
  if (node?.group && node.key) {
    return context?.mergedLeadsByPhoneNumber.get(node.key);
  }
  return undefined;
}

function formatPhoneNumber(phoneNumber?: string) {
  if (!phoneNumber) return "-";
  try {
    const phone = parsePhoneNumberFromString(phoneNumber);
    return phone ? phone.formatInternational() : phoneNumber;
  } catch {
    return phoneNumber;
  }
}

function formatDate(dateString: string) {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function PhoneCellRenderer({
  phoneNumber,
}: {
  phoneNumber?: string;
}) {
  if (!phoneNumber) return "-";

  return (
    <Link
      to={`/messages/sms/inbox/${encodeURIComponent(phoneNumber)}`}
      className="text-primary underline underline-offset-4"
    >
      {formatPhoneNumber(phoneNumber)}
    </Link>
  );
}

function GroupPhoneInnerRenderer({
  node,
  context,
}: ICellRendererParams<PhoneNumberMergeRow, unknown, PhoneNumberMergeGridContext>) {
  if (!node.group || !node.key) return null;

  const lead = context?.mergedLeadsByPhoneNumber.get(node.key);
  const phoneNumber = lead?.phoneNumber || node.key;
  return <PhoneCellRenderer phoneNumber={phoneNumber} />;
}

function ActionsCellRenderer({
  node,
  context,
}: ICellRendererParams<PhoneNumberMergeRow, unknown, PhoneNumberMergeGridContext>) {
  if (!node.group || !node.key) return null;

  return (
    <div className="flex h-full items-center justify-end" onClick={(event) => event.stopPropagation()}>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={`Actions for ${node.key}`}
            disabled={context?.isMerging}
          >
            <MoreHorizontal className="h-4 w-4" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => context?.onMergeSingle(node.key!)}
            disabled={context?.isMerging}
          >
            <GitMerge className="h-4 w-4" />
            Merge
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

export function ContactsPhoneNumberMerge() {
  const { setBreadcrumbs, clearBreadcrumbs } = useBreadcrumb();
  const gridApiRef = useRef<GridApi<PhoneNumberMergeRow> | null>(null);
  const [groups, setGroups] = useState<DuplicateGroup[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isMerging, setIsMerging] = useState(false);
  const [selectedPhoneNumbers, setSelectedPhoneNumbers] = useState<Set<string>>(new Set());

  const fetchDuplicates = useCallback(async () => {
    try {
      const response = await fetch("/api/contacts/phone/merge");
      if (response.ok) {
        const data = await response.json();
        setGroups(data.groups || []);
      } else {
        console.error("Failed to fetch phone number merge duplicates");
      }
    } catch (error) {
      console.error("Failed to fetch phone number merge duplicates:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    setBreadcrumbs([
      { label: "Contacts", path: "/contacts" },
      { label: "Phone Merge", path: "/contacts/phone/merge" },
    ]);

    return () => {
      clearBreadcrumbs();
    };
  }, [setBreadcrumbs, clearBreadcrumbs]);

  useEffect(() => {
    fetchDuplicates();
  }, [fetchDuplicates]);

  const mergePhoneNumbers = useCallback(
    async (phoneNumbers: string[]) => {
      if (phoneNumbers.length === 0 || isMerging) return;

      const confirmed = window.confirm(
        phoneNumbers.length === 1
          ? "Are you sure you want to merge the duplicate contacts for this phone number? This action cannot be undone."
          : `Are you sure you want to merge ${phoneNumbers.length} duplicate phone numbers? This action cannot be undone.`,
      );
      if (!confirmed) return;

      setIsMerging(true);
      try {
        const response = await fetch("/api/contacts/phone/merge", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ phoneNumbers }),
        });

        if (response.ok) {
          setSelectedPhoneNumbers(new Set());
          gridApiRef.current?.deselectAll();
          setIsLoading(true);
          await fetchDuplicates();
        } else {
          console.error("Failed to merge phone number duplicate leads");
        }
      } catch (error) {
        console.error("Failed to merge phone number duplicate leads:", error);
      } finally {
        setIsMerging(false);
      }
    },
    [fetchDuplicates, isMerging],
  );

  const handleMergeSelected = useCallback(() => {
    mergePhoneNumbers([...selectedPhoneNumbers]);
  }, [mergePhoneNumbers, selectedPhoneNumbers]);

  const handleMergeSingle = useCallback(
    (phoneNumber: string) => {
      mergePhoneNumbers([phoneNumber]);
    },
    [mergePhoneNumbers],
  );

  const rowData = useMemo(
    () =>
      groups.flatMap((group) =>
        group.leads.map((lead) => ({
          ...lead,
          groupPhoneNumber: group.phoneNumber,
        })),
      ),
    [groups],
  );

  const mergedLeadsByPhoneNumber = useMemo(
    () =>
      new Map(
        groups.flatMap((group) =>
          group.mergedLead ? [[group.phoneNumber, group.mergedLead] as const] : [],
        ),
      ),
    [groups],
  );

  const gridContext = useMemo<PhoneNumberMergeGridContext>(
    () => ({
      isMerging,
      mergedLeadsByPhoneNumber,
      onMergeSingle: handleMergeSingle,
    }),
    [handleMergeSingle, isMerging, mergedLeadsByPhoneNumber],
  );

  const autoGroupColumnDef = useMemo<ColDef<PhoneNumberMergeRow>>(
    () => ({
      headerName: "Phone",
      minWidth: 220,
      flex: 1,
      cellClass: "font-mono text-sm",
      cellRendererParams: {
        suppressCount: true,
        innerRenderer: GroupPhoneInnerRenderer,
      },
    }),
    [],
  );

  const columnDefs = useMemo<ColDef<PhoneNumberMergeRow>[]>(
    () => [
      {
        field: "groupPhoneNumber",
        rowGroup: true,
        hide: true,
      },
      {
        headerName: "Name",
        flex: 1,
        minWidth: 140,
        valueGetter: ({ data, node, context }) => {
          const lead = getRowLead({ data, node, context });
          if (!lead) return "-";
          const name = getLeadName(lead);
          return name !== "-" ? name : "-";
        },
        cellRenderer: ({ data, node, context }: ICellRendererParams<
          PhoneNumberMergeRow,
          unknown,
          PhoneNumberMergeGridContext
        >) => {
          const lead = getRowLead({ data, node, context });
          if (!lead) return "-";

          const name = getLeadName(lead);
          if (name === "-") return "-";

          if (data) {
            return (
              <Link
                to={`/contacts/${data._id}/edit`}
                className="text-primary underline underline-offset-4"
              >
                {name}
              </Link>
            );
          }

          return (
            <Link
              to={`/contacts/${lead._id}/edit`}
              className="text-primary underline underline-offset-4"
            >
              {name}
            </Link>
          );
        },
      },
      {
        headerName: "Email",
        flex: 1,
        minWidth: 160,
        valueGetter: ({ data, node, context }) => getRowLead({ data, node, context })?.email || "-",
      },
      {
        headerName: "Instagram",
        flex: 1,
        minWidth: 120,
        valueGetter: ({ data, node, context }) => {
          const lead = getRowLead({ data, node, context });
          return lead?.instagram?.igUsername || "-";
        },
        cellRenderer: ({ data, node, context }: ICellRendererParams<
          PhoneNumberMergeRow,
          unknown,
          PhoneNumberMergeGridContext
        >) => {
          const lead = getRowLead({ data, node, context });
          const handle = lead?.instagram?.igUsername;
          const href = handle ? instagramProfileUrlFromHandle(handle) : null;

          if (!handle || !href) return "-";

          return (
            <a
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="text-primary underline underline-offset-4"
            >
              {handle}
            </a>
          );
        },
      },
      {
        headerName: "Manychat",
        flex: 1,
        minWidth: 120,
        valueGetter: ({ data, node, context }) =>
          getRowLead({ data, node, context })?.manychat?.id || "-",
      },
      {
        headerName: "Joined",
        width: 120,
        valueGetter: ({ data, node, context }) => {
          const joinedAt = getRowLead({ data, node, context })?.joinedAt;
          return joinedAt ? formatDate(joinedAt) : "-";
        },
      },
      {
        colId: "actions",
        headerName: "",
        width: 56,
        maxWidth: 56,
        sortable: false,
        filter: false,
        resizable: false,
        suppressMovable: true,
        cellRenderer: ActionsCellRenderer,
      },
    ],
    [],
  );

  const defaultColDef = useMemo<ColDef<PhoneNumberMergeRow>>(
    () => ({
      sortable: false,
      filter: false,
      resizable: true,
      suppressMovable: true,
    }),
    [],
  );

  const handleGridReady = useCallback(({ api }: GridReadyEvent<PhoneNumberMergeRow>) => {
    gridApiRef.current = api;
  }, []);

  const handleSelectionChanged = useCallback((event: RowSelectionChangedEvent<PhoneNumberMergeRow>) => {
    const selectedIds = event.api
      .getSelectedNodes()
      .filter((node) => node.group && node.key)
      .map((node) => node.key!);

    setSelectedPhoneNumbers(new Set(selectedIds));
  }, []);

  const getRowId = useCallback(({ data }: GetRowIdParams<PhoneNumberMergeRow>) => {
    if (!data) return "";
    return data._id;
  }, []);

  const isRowSelectable = useCallback(
    (node: IRowNode<PhoneNumberMergeRow>) => {
      return Boolean(node?.group) && !isMerging;
    },
    [isMerging],
  );

  if (isLoading) {
    return (
      <div className="flex min-h-[40vh] items-center justify-center">
        <Loader2 className="h-12 w-12 animate-spin text-muted-foreground" />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Phone Merge</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            {groups.length} duplicate phone number{groups.length === 1 ? "" : "s"}
            {selectedPhoneNumbers.size > 0 ? ` · ${selectedPhoneNumbers.size} selected` : ""}
          </p>
        </div>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              aria-label="Merge actions"
              disabled={isMerging}
            >
              <MoreHorizontal className="h-4 w-4" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuItem
              onClick={handleMergeSelected}
              disabled={selectedPhoneNumbers.size === 0 || isMerging}
            >
              <GitMerge className="h-4 w-4" />
              Merge
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>

      <div className="ag-theme-quartz w-full" style={{ height: "calc(100vh - 12rem)" }}>
        <AgGridReact<PhoneNumberMergeRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDefaultExpanded={0}
          context={gridContext}
          getRowId={getRowId}
          rowSelection={{
            mode: "multiRow",
            checkboxes: true,
            headerCheckbox: true,
            enableClickSelection: false,
            isRowSelectable,
          }}
          onGridReady={handleGridReady}
          onSelectionChanged={handleSelectionChanged}
          overlayNoRowsTemplate="No duplicate phone numbers found"
          suppressCellFocus
          enableCellTextSelection
        />
      </div>
    </div>
  );
}
